"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Boxes,
  Bell,
  ClipboardCheck,
  UserCircle,
  Mic,
  CalendarClock,
} from "lucide-react";

const STEPS = [
  {
    step: "01",
    title: "실시간 피드백 신청",
    description: "보컬·연주 영상을 올리고 실시간 1:1 피드백 옵션을 선택합니다.",
    icon: ClipboardCheck,
  },
  {
    step: "02",
    title: "프로 매칭",
    description: "전문가가 신청을 확인하고 레슨 일정을 조율합니다.",
    icon: UserCircle,
  },
  {
    step: "03",
    title: "일정 조율",
    description: "매칭된 프로와 가능한 시간을 조율해 실시간 세션 일정을 정합니다.",
    icon: CalendarClock,
  },
  {
    step: "04",
    title: "강의실 초대",
    description: "강사가 강의실을 열면 학생에게 초대 알림이 전송됩니다.",
    icon: Bell,
  },
  {
    step: "05",
    title: "강의실 입장",
    description:
      "나만의 아바타로 강의실에 들어가 음성으로 소통하고, 강사 화이트보드·무대 호출로 레슨을 진행합니다.",
    icon: Boxes,
  },
] as const;

interface RealtimeFeedbackSectionProps {
  showEnterButton?: boolean;
  onEnter?: () => void;
  entering?: boolean;
  compact?: boolean;
}

export default function RealtimeFeedbackSection({
  showEnterButton = true,
  onEnter,
  entering = false,
  compact = false,
}: RealtimeFeedbackSectionProps) {
  return (
    <section
      className={
        compact
          ? ""
          : "bg-[#12121a] py-16 sm:py-20 md:py-24 border-t border-white/5"
      }
    >
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
        <div className="flex flex-col gap-3">
          <span className="text-base font-semibold uppercase tracking-[0.24em] text-[#A78BFA]">
            Realtime Feedback
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-white">
            실시간 1:1 피드백, 이렇게 진행됩니다
          </h2>
          <p className="max-w-2xl text-base text-gray-300 sm:text-lg">
            프로와 1:1로 만나는 실시간 음악 피드백입니다. 아바타 강의실에서
            음성·화이트보드로 진행되며, 신청부터 입장까지 한 흐름으로
            이어집니다.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {STEPS.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ delay: index * 0.08, duration: 0.5 }}
                className="relative rounded-2xl border border-white/10 bg-white/[0.04] p-5 sm:p-6"
              >
                <span className="text-xs font-semibold tracking-widest text-[#A78BFA]">
                  STEP {item.step}
                </span>
                <div className="mt-3 mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-purple-500/15 text-[#C4B5FD]">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/65">
                  {item.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-8 rounded-2xl border border-purple-500/20 bg-gradient-to-r from-purple-950/40 to-indigo-950/30 p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <Mic className="h-5 w-5 text-[#A78BFA] mt-0.5 shrink-0" />
            <p className="text-sm sm:text-base text-white/80 leading-relaxed">
              강의실에서는 <strong className="text-white">음성 대화</strong>,
              <strong className="text-white"> 3D 아바타 이동</strong>,
              <strong className="text-white"> 강사 화이트보드</strong>,
              <strong className="text-white"> 무대 호출</strong>로 레슨이
              이루어집니다. 입장 전 프로필에서 아바타를 미리 만들어 두세요.
            </p>
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
          <Link href="/realtime-feedback/demo" className="flex-1 sm:flex-none">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full sm:w-auto px-6 py-3 rounded-full bg-gradient-to-r from-[#8B5CF6] to-[#6366F1] text-white font-semibold text-sm sm:text-base shadow-lg flex items-center justify-center gap-2"
            >
              3분 체험해보기
              <Boxes className="h-4 w-4" />
            </motion.button>
          </Link>

          <Link href="/expert-feedback?option=3" className="flex-1 sm:flex-none">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full sm:w-auto px-6 py-3 rounded-full border border-[#A78BFA]/50 bg-[#A78BFA]/10 text-white font-semibold text-sm sm:text-base flex items-center justify-center gap-2"
            >
              실시간 피드백 신청하기
              <ArrowRight className="h-4 w-4" />
            </motion.button>
          </Link>

          {showEnterButton && (
            <motion.button
              type="button"
              onClick={onEnter}
              disabled={entering}
              whileHover={{ scale: entering ? 1 : 1.02 }}
              whileTap={{ scale: entering ? 1 : 0.98 }}
              className="w-full sm:w-auto px-6 py-3 rounded-full border border-[#A78BFA]/50 bg-[#A78BFA]/10 text-white font-semibold text-sm sm:text-base flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Boxes className="h-4 w-4" />
              {entering ? "확인 중..." : "강의실 입장하기"}
            </motion.button>
          )}
        </div>
      </div>
    </section>
  );
}
